import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Workflow,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Search,
  ChevronRight,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { useWorkflowListQuery } from '@/client/api/workflows/workflows.query';
import { useListFilters } from '@/client/hooks/useListFilters';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  WorkflowAccessDenied,
  formatDateTime,
  isN8nUnconfigured,
} from '@/client/components/admin/Workflows/workflowUi';

type ActiveFilter = '' | 'true' | 'false';

const WorkflowsListPage = () => {
  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Workflows' },
  ]);

  const viewPerm = useHasPermission('workflow.view');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  // `active` and the search term persist in the URL (?active=&q=) across reloads.
  const { filters, handleFilterChange, searchTerm, handleSearch } = useListFilters<{
    active: ActiveFilter;
  }>({ initialFilters: { active: '' } });
  const active = filters.active;
  const search = searchTerm;

  const params = useMemo(
    () => ({
      ...(active === 'true' ? { active: true as const } : {}),
      ...(active === 'false' ? { active: false as const } : {}),
      limit: 250,
    }),
    [active],
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useWorkflowListQuery(
    params,
    canView,
  );

  const workflows = useMemo(() => {
    const list = data?.workflows ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        w.id.toLowerCase().includes(q) ||
        w.webhook_paths?.some((p) => p.toLowerCase().includes(q)),
    );
  }, [data?.workflows, search]);

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-56" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <WorkflowAccessDenied />
        </div>
      </div>
    );
  }

  const n8nDown = isError && isN8nUnconfigured(error);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Workflow className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">n8n Workflows</h1>
              <p className="mt-0.5 text-sm text-gray-500">
                Catalog and activation state for the n8n Cloud tenant.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2"
          >
            {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
        </div>

        {n8nDown && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">n8n is not configured on this environment</p>
              <p className="mt-0.5 text-amber-800/80">
                Set <code className="rounded bg-amber-100 px-1">FF_N8N_API_BASE</code> and{' '}
                <code className="rounded bg-amber-100 px-1">FF_N8N_ADMIN_API_KEY</code>. Scheduler,
                validator, and webhook pages still work.
              </p>
            </div>
          </div>
        )}

        {isError && !n8nDown && (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {getApiErrorMessage(error, 'Failed to load workflows')}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by name, id, or webhook path…"
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-gray-400"
            />
          </div>
          <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
            {(
              [
                ['', 'All'],
                ['true', 'Active'],
                ['false', 'Inactive'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value || 'all'}
                type="button"
                onClick={() => handleFilterChange('active', value)}
                className={`rounded-md px-3 py-1.5 ${
                  active === value ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          {isLoading ? (
            <div className="space-y-3 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : workflows.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-gray-400">
              No workflows match this filter.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Nodes</th>
                  <th className="px-4 py-3 font-medium">Webhook paths</th>
                  <th className="px-4 py-3 font-medium">Updated</th>
                  <th className="px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody>
                {workflows.map((w) => (
                  <tr key={w.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/workflows/${w.id}`}
                        className="font-medium text-gray-900 hover:underline"
                      >
                        {w.name}
                      </Link>
                      <p className="mt-0.5 font-mono text-xs text-gray-400">{w.id}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className={
                          w.active
                            ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                            : 'border-gray-200 bg-gray-50 text-gray-600'
                        }
                      >
                        {w.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{w.node_count}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(w.webhook_paths ?? []).length === 0 && (
                          <span className="text-gray-400">—</span>
                        )}
                        {(w.webhook_paths ?? []).map((p) => (
                          <code
                            key={p}
                            className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
                          >
                            {p}
                          </code>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDateTime(w.updated_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/workflows/${w.id}`}
                        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
                      >
                        Open <ChevronRight className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowsListPage;
